import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Checkbox,
  VStack,
  HStack,
  Text,
  Box,
  Radio,
  RadioGroup,
  Stack,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

export default function AddDayOffModalChakra({ workers, categories, dayOff, onClose, onSubmit }) {
  const isEditing = !!dayOff;

  const [formData, setFormData] = useState({
    name: '',
    category: 'vacation',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    isPartialDay: false,
    startTime: '09:00',
    endTime: '17:00',
    notes: '',
    applyToUserIds: []
  });

  const [applyToMode, setApplyToMode] = useState('select');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [workerSearch, setWorkerSearch] = useState('');
  const [showWorkerDropdown, setShowWorkerDropdown] = useState(false);

  const workerDropdownRef = useRef(null);

  useEffect(() => {
    if (dayOff) {
      setFormData({
        name: dayOff.name,
        category: dayOff.category || 'vacation',
        startDate: dayOff.start_date,
        endDate: dayOff.end_date,
        isPartialDay: !dayOff.full_day,
        startTime: dayOff.start_time || '09:00',
        endTime: dayOff.end_time || '17:00',
        notes: dayOff.notes || '',
        applyToUserIds: [dayOff.worker_id]
      });
      setApplyToMode('select');
    } else if (workers.length > 0) {
      setFormData(prev => ({ ...prev, applyToUserIds: [workers[0].id] }));
    }
  }, [dayOff, workers]);

  useEffect(() => {
    // Update applyToUserIds based on mode
    if (applyToMode === 'all') {
      setFormData(prev => ({ ...prev, applyToUserIds: workers.map(w => w.id) }));
    } else if (applyToMode === 'select' && workers.length > 0 && formData.applyToUserIds.length === 0) {
      setFormData(prev => ({
        ...prev,
        applyToUserIds: [workers[0].id]
      }));
    }
  }, [applyToMode, workers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(event.target)) {
        setShowWorkerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isSingleDay = formData.startDate === formData.endDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Please enter a name');
      return;
    }

    if (formData.applyToUserIds.length === 0) {
      setError('Please select at least one worker');
      return;
    }

    setSaving(true);

    try {
      if (isEditing) {
        await onSubmit(dayOff.id, formData);
      } else {
        await onSubmit(formData);
      }
    } catch (err) {
      setError(err.message || 'Failed to save day off');
    } finally {
      setSaving(false);
    }
  };

  const handleWorkerToggle = (workerId) => {
    setFormData(prev => {
      const ids = prev.applyToUserIds;
      if (ids.includes(workerId)) {
        return { ...prev, applyToUserIds: ids.filter(id => id !== workerId) };
      } else {
        return { ...prev, applyToUserIds: [...ids, workerId] };
      }
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      holiday: 'red',
      vacation: 'blue',
      personal: 'purple',
      sick: 'orange',
      other: 'gray'
    };
    return colors[category] || 'gray';
  };

  const filteredWorkers = workers.filter(w =>
    w.name.toLowerCase().includes(workerSearch.toLowerCase())
  );

  const getSelectedWorkersText = () => {
    if (formData.applyToUserIds.length === 0) return 'Select workers...';
    if (formData.applyToUserIds.length === 1) {
      const worker = workers.find(w => w.id === formData.applyToUserIds[0]);
      return worker?.name || 'Select workers...';
    }
    return `${formData.applyToUserIds.length} workers selected`;
  };

  const removeWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      applyToUserIds: prev.applyToUserIds.filter(id => id !== workerId)
    }));
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        borderRadius="2xl"
        boxShadow="2xl"
        maxH={{ base: "calc(100dvh - 24px)", md: "85vh" }}
        my="auto"
        mx={{ base: 3, md: 0 }}
      >
        <ModalHeader
          borderTopRadius="2xl"
          bg="linear-gradient(135deg, rgba(109,40,217,0.08), rgba(14,165,233,0.08))"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            {isEditing ? 'Edit Day Off' : 'Add Day Off'}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <form onSubmit={handleSubmit}>
          <ModalBody py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }} overflowY="auto" maxH={{ base: "calc(100dvh - 180px)", md: "calc(85vh - 180px)" }}>
            <VStack spacing={5} align="stretch">
              {error && (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Name */}
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Name
                </FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Christmas, Vacation, Doctor Appointment"
                  size="lg"
                  borderWidth="2px"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }}
                />
              </FormControl>

              {/* Category */}
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Category
                </FormLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  size="lg"
                  borderWidth="2px"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }}
                >
                  {Object.entries(categories).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </FormControl>

              {/* Date Range */}
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Date Range
                </FormLabel>
                <HStack spacing={3} align="stretch" flexDirection={{ base: "column", sm: "row" }}>
                  <VStack align="start" flex={1} spacing={1}>
                    <Text fontSize="xs" color="gray.600">Start Date</Text>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      size="lg"
                      borderWidth="2px"
                    />
                  </VStack>
                  <VStack align="start" flex={1} spacing={1}>
                    <Text fontSize="xs" color="gray.600">End Date</Text>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      min={formData.startDate}
                      size="lg"
                      borderWidth="2px"
                    />
                  </VStack>
                </HStack>
                <FormHelperText>
                  Select the same date for a single day, or a range for multiple days
                </FormHelperText>
              </FormControl>

              {/* Partial Day Toggle (only for single day) */}
              {isSingleDay && (
                <FormControl>
                  <Checkbox
                    isChecked={formData.isPartialDay}
                    onChange={(e) => setFormData({ ...formData, isPartialDay: e.target.checked })}
                    colorScheme="blue"
                    size="lg"
                  >
                    <Text fontWeight="semibold" color="gray.700">
                      Partial Day (specific hours)
                    </Text>
                  </Checkbox>
                </FormControl>
              )}

              {/* Time Pickers (only if partial day) */}
              {formData.isPartialDay && isSingleDay && (
                <FormControl>
                  <FormLabel fontWeight="semibold" color="gray.700">
                    Time Range
                  </FormLabel>
                  <HStack spacing={3} align="stretch" flexDirection={{ base: "column", sm: "row" }}>
                    <VStack align="start" flex={1} spacing={1}>
                      <Text fontSize="xs" color="gray.600">Start Time</Text>
                      <Input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                        size="lg"
                        borderWidth="2px"
                      />
                    </VStack>
                    <VStack align="start" flex={1} spacing={1}>
                      <Text fontSize="xs" color="gray.600">End Time</Text>
                      <Input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                        size="lg"
                        borderWidth="2px"
                      />
                    </VStack>
                  </HStack>
                </FormControl>
              )}

              {/* Notes */}
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Notes (optional)
                </FormLabel>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any additional details..."
                  size="lg"
                  borderWidth="2px"
                />
              </FormControl>

              {/* Apply To Workers (only when adding, not editing) */}
              {!isEditing && (
                <>
                  <Divider />
                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold" color="gray.700" mb={3}>
                      Apply to Workers
                    </FormLabel>

                    {/* Mode Selection */}
                    <RadioGroup value={applyToMode} onChange={setApplyToMode} mb={3}>
                      <Stack spacing={2}>
                        <Radio value="select" colorScheme="blue" size="lg">
                          <Text fontWeight="medium">Select Workers</Text>
                        </Radio>
                        <Radio value="all" colorScheme="blue" size="lg">
                          <Text fontWeight="medium">All Workers ({workers.length})</Text>
                        </Radio>
                      </Stack>
                    </RadioGroup>

                    {/* Select Workers - Searchable Multi-Select */}
                    {applyToMode === 'select' && (
                      <Box position="relative" ref={workerDropdownRef}>
                        {/* Selected Workers Display */}
                        {formData.applyToUserIds.length > 0 && (
                          <Wrap spacing={2} mb={2}>
                            {formData.applyToUserIds.map(workerId => {
                              const worker = workers.find(w => w.id === workerId);
                              return worker ? (
                                <WrapItem key={workerId}>
                                  <Tag size="lg" colorScheme="blue" borderRadius="full">
                                    <TagLabel>{worker.name}</TagLabel>
                                    <TagCloseButton onClick={() => removeWorker(workerId)} />
                                  </Tag>
                                </WrapItem>
                              ) : null;
                            })}
                          </Wrap>
                        )}

                        {/* Search Input */}
                        <Input
                          value={workerSearch}
                          onChange={(e) => {
                            setWorkerSearch(e.target.value);
                            setShowWorkerDropdown(true);
                          }}
                          onFocus={() => setShowWorkerDropdown(true)}
                          placeholder="Search and select workers..."
                          size="lg"
                          borderWidth="2px"
                          _hover={{ borderColor: "gray.400" }}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                          }}
                        />

                        {/* Dropdown */}
                        {showWorkerDropdown && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            mt={1}
                            bg="white"
                            border="2px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            boxShadow="lg"
                            maxH="250px"
                            overflowY="auto"
                            zIndex={1000}
                          >
                            {filteredWorkers.length > 0 ? (
                              <VStack spacing={0} align="stretch">
                                {filteredWorkers.map(worker => (
                                  <Box
                                    key={worker.id}
                                    px={4}
                                    py={3}
                                    cursor="pointer"
                                    _hover={{ bg: "blue.50" }}
                                    bg={formData.applyToUserIds.includes(worker.id) ? "blue.100" : "white"}
                                    onClick={() => {
                                      handleWorkerToggle(worker.id);
                                      setWorkerSearch('');
                                    }}
                                  >
                                    <HStack>
                                      <Checkbox
                                        isChecked={formData.applyToUserIds.includes(worker.id)}
                                        colorScheme="blue"
                                        pointerEvents="none"
                                      />
                                      <Text fontSize="sm">{worker.name}</Text>
                                    </HStack>
                                  </Box>
                                ))}
                              </VStack>
                            ) : (
                              <Box px={4} py={3}>
                                <Text fontSize="sm" color="gray.500">
                                  No workers found
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* All Workers Info */}
                    {applyToMode === 'all' && (
                      <Alert status="info" borderRadius="lg">
                        <AlertIcon />
                        <AlertDescription>
                          This will create a day off for all {workers.length} workers
                        </AlertDescription>
                      </Alert>
                    )}
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            borderBottomRadius="2xl"
          >
            <HStack spacing={3} width="full" flexDirection={{ base: "column-reverse", sm: "row" }}>
              <Button
                variant="outline"
                size="lg"
                onClick={onClose}
                flex={1}
                borderWidth="2px"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                isLoading={saving}
                loadingText="Saving..."
                flex={1}
              >
                {isEditing ? 'Update Day Off' : 'Add Day Off'}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
